import React, { useEffect, useState } from "react";
import ApiClient from "../services/fuelandmoreapi";
import type { TCurrentVessel } from "@/types";
import { config } from "../config";
import AddVessel from "./AddVessel";
import BinIcon from "./BinIcon";
import "./VesselList.css";

interface VesselListProps {
  vessels: string[];
  fleetName: string;
  clientFleetId: string;
}

const VesselList: React.FC<VesselListProps> = ({
  vessels,
  fleetName,
  clientFleetId,
}) => {
  const [vesselDetails, setVesselDetails] = useState<TCurrentVessel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddVessel, setShowAddVessel] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null); // IMO of vessel to delete

  const { apiBaseUrl } = config;
  const apiUrl = apiBaseUrl;

  const getRiskScoreText = (score: number | null) => {
    if (score === null || score === undefined) return "N/A";
    if (score > 70) return "High";
    if (score > 30) return "Medium";
    return "Low";
  };

  const getRiskColor = (score: number | null) => {
    if (score === null || score === undefined) return "";
    if (score > 70) return "risk-score-red";
    if (score > 30) return "risk-score-amber";
    return "risk-score-green";
  };

  useEffect(() => {
    const fetchVesselDetails = async () => {
      try {
        const apiClient = new ApiClient(apiUrl);
        const fetchedVessels: TCurrentVessel[] = [];

        for (const imo of vessels) {
          const [vesselStatic, riskScoreResponse] = await Promise.all([
            apiClient.getVesselStatic(imo),

            apiClient.getVesselRiskScore(imo),
          ]);

          let riskScore: any = null;
          let score: number | null = null;

          if (riskScoreResponse.data != "") {
            riskScore = riskScoreResponse.data.RiskScores.find(
              (r: any) => r.RiskPeriodInMonths === 6
            );
            score = riskScore ? riskScore.TotalRiskScore : null;
          }

          const vesselWithPosition: TCurrentVessel = {
            ...vesselStatic.data,

            riskScore: typeof score === "number" ? score : 0,
            riskScoreDetails: riskScoreResponse.data,
          };

          fetchedVessels.push(vesselWithPosition);
        }
        setVesselDetails(fetchedVessels);
      } catch (err) {
        console.error("Error fetching vessel details:", err);

        setError("Failed to fetch vessel details.");
      } finally {
        setLoading(false);
      }
    };
    fetchVesselDetails();
  }, [vessels, apiUrl]);

  const handleVesselAdded = async (newVessel: TCurrentVessel) => {
    setVesselDetails((prev) => [...prev, newVessel]);
    setShowAddVessel(false);
  };

  const handleDeleteVessel = async (imo: string) => {
    try {
      const apiClient = new ApiClient(apiUrl);
      await apiClient.removeVesselFromClientFleet(clientFleetId, imo);
      setVesselDetails((prev) => prev.filter((v) => v.imo !== imo));
      setConfirmDelete(null);
    } catch (err) {
      setError("Failed to remove vessel.");
      setConfirmDelete(null);
    }
  };

  if (loading) {
    return <div>Loading vessel details...</div>;
  }

  if (error) {
    return <div className="vessel-list-error">{error}</div>;
  }

  return (
    <div className="vessel-list-container">
      <h3 className="vessel-list-title">Vessels in {fleetName}</h3>

      <table className="vessel-list-table">
        <thead>
          <tr>
            <th>IMO</th>
            <th>Name</th>
            <th>Type</th>
            <th>DWT</th>
            <th>Risk</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {vesselDetails.map((vessel) => (
            <tr key={vessel.imo}>
              <td>{vessel.imo}</td>
              <td>{vessel.name}</td>
              <td>{vessel.type}</td>
              <td>{vessel.dwt}</td>
              <td>
                {vessel.riskScore !== undefined
                  ? getRiskScoreText(vessel.riskScore)
                  : ""}
              </td>
              <td style={{ textAlign: "center" }}>
                <button
                  className="bin-btn"
                  title="Remove Vessel"
                  onClick={(e) => {
                    e.stopPropagation();
                    setConfirmDelete(vessel.imo);
                  }}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                  }}
                >
                  <BinIcon color="black" />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="add-vessel-button-container">
        {showAddVessel ? (
          <AddVessel
            fleetName={fleetName}
            clientFleetId={clientFleetId}
            onCancel={() => setShowAddVessel(false)}
            onAddVessel={(clientFleetId, newVessel) => {
              setVesselDetails((prev) => [...prev, newVessel]);
              setShowAddVessel(false);
            }}
          />
        ) : (
          <button
            className="primary-button"
            onClick={() => setShowAddVessel(true)}
          >
            + Add Vessel
          </button>
        )}
      </div>

      {/* Confirm delete modal */}
      {confirmDelete && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-message">
              Are you sure you want to remove vessel <b>{confirmDelete}</b> from
              this fleet?
            </div>
            <div className="modal-actions">
              <button
                className="modal-btn confirm"
                onClick={() => handleDeleteVessel(confirmDelete!)}
              >
                Yes
              </button>
              <button
                className="modal-btn cancel"
                onClick={() => setConfirmDelete(null)}
              >
                No
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VesselList;
